<template>
  <div id="app" class="screen-height" v-if="rememberMeLoaded" :lang="$store.state.language">
    <transition name="fade">
      <div class="cookie p-20 d-flex align-items-center" v-if="!understood">
        <div class="mr-auto">
          <span>{{ $t('messages.understood_text_1_pre') }} </span>
          <b-link to="/kickstarter">{{ $t('messages.understood_text_1_pre_ks') }}</b-link>
          <span> {{ $t('messages.understood_text_1') }} </span>
          <b-link to="/store">{{ $t('messages.understood_text_1_post_games') }}</b-link>
          <span> {{ $t('messages.understood_text_1_post_text') }}</span>
          <br />
          <span>{{ $t('messages.understood_text_2') }} </span>
          <b-link to="/create">{{ $t('messages.understood_text_2_after') }}</b-link>.
        </div>
        <div class="d-flex-responsive align-items-center">
          <div class="pb-1"><b-button variant="primary" @click="understoodIt">{{ $t('messages.understood_it') }}</b-button></div>
        </div>
      </div>
    </transition>
    <transition name="fade">
      <div class="cookie p-20 d-flex align-items-center" v-if="!hasAcceptCookies">
        <div class="mr-auto">{{ $t('messages.cookie_1') }}<br />{{ $t('messages.cookie_2') }}</div>
        <div class="d-flex-responsive align-items-center">
          <div class="mr-3 pb-1"><b-link :to="{ path: '/privacy' }">{{ $t('messages.cookie_info') }}</b-link></div>
          <div class="mr-3 pb-1"><b-button @click="refuseCookies">{{ $t('messages.cookie_refuse') }}</b-button></div>
          <div class="pb-1"><b-button variant="primary" @click="acceptCookies">{{ $t('messages.cookie_accept') }}</b-button></div>
        </div>
      </div>
    </transition>
    <div class="page-content">
      <navigation></navigation>
      <b-container fluid class="full-height m-b-m-fix">
        <div class="alert alert-success">
          {{ $t('messages.shipping_tomorrow') }}
        </div>
        <notification></notification>
        <router-view :key="$route.fullPath" />
      </b-container>
    </div>
    <footer class="footer sticky-footer py-3 m-t-fix">
      <div class="text-center d-flex-responsive justify-content-center align-items-center">
        <div class="d-flex justify-content-center align-items-center">
          <b-link :to="{ path: '/about' }" class="mr-3">{{ $t('messages.about') }}</b-link>
          <b-link :to="{ path: '/disclaimer/participate' }" class="mr-3">{{ $t('messages.disclaimer_participate') }}</b-link>
        </div>
        <div class="d-flex justify-content-center align-items-center">
          <b-link :to="{ path: '/disclaimer/create' }" class="mr-3">{{ $t('messages.disclaimer_create') }}</b-link>
          <b-link :to="{ path: '/shipping' }" class="mr-3">{{ $t('messages.shipping_pickup') }}</b-link>
          <b-link :to="{ path: '/return' }" class="mr-3">{{ $t('messages.return') }}</b-link>
        </div>
        <div class="d-flex justify-content-center align-items-center mr-3">
          <b-link :to="{ path: '/terms' }" class="mr-3">{{ $t('messages.terms_conditions') }}</b-link>
          <b-link :to="{ path: '/privacy' }" class="mr-3">{{ $t('messages.privacy_statement') }}</b-link>
          <b-link :to="{ path: '/contact' }" class="mobile-mr-3">{{ $t('messages.contact') }}</b-link>
        </div>
        <div class="d-flex justify-content-center align-items-center mobile-mt">
          <span class="mr-2">Copyright {{ new Date().getFullYear() }} &copy; CrowdFinder</span>
          <img :src="getPaymentMethodImage('bancontact')" class="payment-logo mr-2" />
          <img :src="getPaymentMethodImage('ideal')" class="payment-logo mr-2" />
          <font-awesome-icon icon="money-check-alt" class="red payment-logo transfer"></font-awesome-icon>
          <font-awesome-icon :icon="['fab', 'paypal']" class="red payment-logo transfer"></font-awesome-icon>
          <font-awesome-icon icon="credit-card" class="red payment-logo transfer"></font-awesome-icon>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import Navigation from './components/Navigation'
import Notification from './components/Notification'

import { userService } from './services/user.service'

import { defaultMixin } from './mixin/default'

export default {
  name: 'App',
  metaInfo: function () {
    return {
      title: 'CrowdFinder',
      meta: [
        {charset: 'utf-8'},
        {name: 'viewport', content: 'width=device-width, initial-scale=1'},
        {
          'property': 'og:title',
          'content': 'CrowdFinder',
          'vmid': 'og:title'
        },
        {
          'property': 'og:image',
          'content': 'https://www.crowdfinder.be/image/logo_opengraph.png',
          'vmid': 'og:image'
        },
        {
          'property': 'og:site_name',
          'content': 'CrowdFinder',
          'vmid': 'og:site_name'
        },
        {
          'property': 'og:description',
          'content': 'CrowdFinder - The place to be for the best deals for board games',
          'vmid': 'og:description'
        },
        {
          'property': 'og:url',
          'content': 'https://www.crowdfinder.be' + this.$route.fullPath,
          'vmid': 'og:url'
        },
        {
          'property': 'og:type',
          'content': 'website',
          'vmid': 'og:type'
        }
      ]
    }
  },
  mixins: [ defaultMixin ],
  components: { Navigation, Notification },
  data: function () {
    return {
      rememberMeLoaded: !(this.$cookies.get('cf_remember_me') !== null),
      hasAcceptCookies: this.$cookies.get('cf_enable') !== null,
      understood: this.$cookies.get('cf_understood') !== null
    }
  },
  methods: {
    getPaymentMethodImage: function (paymentMethod) {
      return require('../src/assets/payment/' + paymentMethod + '.png')
    },
    acceptCookies: function () {
      this.$cookies.set('cf_enable', true, '1y')
      this.hasAcceptCookies = true
    },
    refuseCookies: function () {
      this.$cookies.set('cf_enable', false, '1y')
      this.hasAcceptCookies = true
    },
    understoodIt: function () {
      this.$cookies.set('cf_understood', true, '1y')
      this.understood = true
    }
  },
  watch: {
    $route: function (to, from) {
      this.$store.commit('clear')
    }
  },
  created: function () {
    let cookies = this.$cookies
    let cookie = cookies.get('cf_remember_me')
    if (cookie) {
      let self = this
      let store = this.$store
      let i18n = this.$i18n

      let split = cookie.split('.')
      let userId = split[0]
      let hash = split[1]

      userService.loginRemember(userId, hash).then((response) => {
        store.commit('userId', response.id)
        store.commit('userName', response.fullname)
        store.commit('userRoles', response.roles)
        store.commit('userTotalProjects', response.totalProjects)
        store.commit('lat', response.lat)
        store.commit('lng', response.lng)
        store.commit('city', response.city)
        store.commit('country', response.country)
        store.commit('discounted', response.discounted)

        let locale = response.language
        store.commit('language', locale)
        i18n.locale = locale
        self.rememberMeLoaded = true
      }, () => {
        userService.logout()
        cookies.remove('cf_remember_me')
        self.rememberMeLoaded = true
      })
    }
  }
}
</script>

<style>
  body {
    overflow-anchor: none;
  }

  .clear {
    clear: both;
  }

  .card__wrapper {
    padding: 10px;
  }

  .card__gutter {
    margin: 10px 10px 5px !important;
    padding: 0.75rem 1.25rem !important;
  }

  .page-content {
    flex: 1 0 auto;
  }

  .sticky-footer {
    flex-shrink: unset;
  }

  .no-messages-margin {
    margin: 0px !important;
  }

  .m-b-m-fix {
    margin-bottom: -205px;
  }

  .m-t-fix {
    margin-top: 205px;
  }

  @media screen and (min-width: 768px) {
    .m-b-m-fix {
      margin-bottom: -191px;
    }

    .m-t-fix {
      margin-top: 191px;
    }
  }

  .mobile-mt {
    margin-top: 10px;
  }

  @media screen and (min-width: 901px) {
    .mobile-mt {
      margin-top: 0px;
    }

    .mobile-mr-3 {
      margin-right: 10px;
    }
  }

  @media screen and (min-width: 1058px) {
    .m-b-m-fix {
      margin-bottom: -149px;
    }

    .m-t-fix {
      margin-top: 149px;
    }
  }

  .card {
    background: #FFFFFF;
    border: 1px #D3D3D3 solid;
    border-radius: 2px;
    margin-bottom: 20px;
  }

  .card p {
    margin: 0px;
  }

  .card__margin p {
    margin-bottom: 1rem !important;
  }

  .card__image {
    height: 0;
    overflow: hidden;
    padding-top: 56.24%;
    background: #FFFFFF;
    position: relative;
  }

  .card__image__inside {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  .card__title, .card__title__border, .text__header {
    font-weight: 400;
    font-size: 20px;
    padding: 20px 20px 10px;
    margin-bottom: 0px;
    line-height: 1.5;
  }

  .card__title div {
    -ms-word-break: break-word;
    word-break: break-word;
  }

  .card__title__border {
    border-bottom: 1px #D3D3D3 solid;
  }

  .card__info {
    padding: 10px 15px;
    text-align: center;
    color: #FFFFFF;
  }

  .card__description {
    padding: 0px 20px 20px;
    border-bottom: 1px #D3D3D3 solid;
  }

  .card__box-icons {
    float: left;
    padding: 20px 10px 20px 20px;
  }

  .card__price {
    float: right;
    text-align: right;
    padding: 20px 20px 20px 10px;
  }

  .card__price p {
    margin-bottom: 5px;
  }

  .card__bottom__flex {
    display: -webkit-flex; /* Safari */
    display: flex;
    width: 100%;
  }

  .card__bottom {
    padding: 20px;
  }

  .card__scroll {
    max-height: 500px;
    overflow-x: hidden;
    overflow-y: auto;
  }

  .card__scroll__hide {
    overflow-y: scroll;
  }

  .card__scroll__hide::-webkit-scrollbar {
    visibility: hidden;
  }

  .overflow-x {
    overflow-x: auto !important;
  }

  .overflow-y {
    overflow-y: auto;
  }

  .striked {
    text-decoration: line-through;
  }

  .strong {
    font-weight: bold;
  }

  .no-margin {
    margin: 0px !important;
  }

  .box-icon {
    display: block;
    margin-bottom: 5px;
  }

  .box-icon-wrapper {
    display: inline-block;
    width: 18px;
  }

  .box-icon svg {
    vertical-align: middle;
  }

  .box-icon span {
    vertical-align: middle;
  }

  .checkboxNoMargin {
    margin-right: 0px;
    float: left;
  }

  .checkboxLineHeight {
    line-height: 24px;
  }

  .buttonNoPadding {
    padding: 0px 0px 1.5px;
  }

  .noPadding {
    padding: 0px !important;
  }

  .noBackground {
    background: none !important;
  }

  .full-width {
    width: 100% !important;
  }

  .full-height {
    min-height: 100% !important;
    height: 100% !important;
  }

  .default-height {
    height: inherit !important;
  }

  .screen-height {
    display: flex;
    flex-flow: column;
    height: 100%;
  }

  .p-20 {
    padding: 20px !important;
  }

  .pt-20 {
    padding-top: 20px !important;
  }

  .pl-20 {
    padding-left: 20px !important;
  }

  .pr-20 {
    padding-right: 20px !important;
  }

  .pb-20 {
    padding-bottom: 20px !important;
  }

  .p-10 {
    padding: 10px !important;
  }

  .pt-10 {
    padding-top: 10px !important;
  }

  .pl-10 {
    padding-left: 10px !important;
  }

  .pr-10 {
    padding-right: 10px !important;
  }

  .pb-10 {
    padding-bottom: 10px !important;
  }

  .m-10 {
    margin: 10px;
  }

  .mb-0 {
    margin-bottom: 0px !important;
  }

  .mt-m-1 {
    margin-top: -1px;
  }

  .mr-m-15 {
    margin-right: -15px;
  }

  .pointer {
    cursor: pointer;
  }

  .vertical-align-top {
    vertical-align: top;
  }

  .text-inherit {
    color: inherit !important;
  }

  .tooltip {
    pointer-events: none;
  }

  .fade-enter-active, .fade-leave-active {
    transition: opacity .5s;
  }

  .fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
    opacity: 0;
  }

  .payment-logo {
    height: 25px !important;
  }

  .transfer {
    width: 35px !important;
  }

  .alert {
    margin: 10px;
  }
</style>

<style lang="scss">
  @import "./assets/scss/custom-bootstrap.scss";
  @import "~bootstrap/scss/bootstrap.scss";
</style>



// WEBPACK FOOTER //
// src/App.vue